import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ApiService } from 'app/lib/api.service';
import { LogService } from 'app/lib/log.service';
import { ModalService } from 'app/lib/modal.service';
import { Reads } from 'app/models/course';
import { MultiAutoItem } from 'app/models/multiautoitem';
import { Organization } from 'app/models/organization';

@Component({
    selector: 'app-course-admin-reads-edit',
    templateUrl: './course-admin-reads-edit.component.html',
    styleUrls: ['./course-admin-reads-edit.component.scss'],
    standalone: false,
})
export class CourseAdminReadsEditComponent implements OnInit {
    @ViewChild('input') fileInput: any;
    readsEditForm: FormGroup;
    readsId: number;
    readsKey: string;
    title: string;
    mode: string;
    // course: Course;
    reads: Reads;
    isLoaded: boolean = false;
    config: any;
    panelState: Array<boolean>;
    submitButtonPressed: boolean = false;
    errors: string = '';
    categoryList: any;
    showTitleFrench = false;
    showTitleSpanish = false;
    showError: boolean = false;
    fileError: boolean = false;
    seriesPageType: string;
    multiSelOrgs: Array<MultiAutoItem> = [];
    organizations: Organization[];
    isFileLoading: boolean = false;
    constructor(
        private translate: TranslateService,
        private formBuilder: FormBuilder,
        private api: ApiService,
        private log: LogService,
        private modalService: ModalService,
        private route: ActivatedRoute,
        private router: Router
    ) {
        // this.readsId = data?.data?.seriesId || null;
        // this.seriesPageType = data?.data?.seriesPageType || '';
        const paramId = this.route.snapshot.paramMap.get('id');
        this.readsId = paramId ? +paramId : null;
        this.seriesPageType =
            this.route.snapshot.queryParamMap.get('seriesPageType') || '';
        this.panelState = [];

        if (this.readsId) {
            this.title = 'Edit this Read';
            this.mode = 'update';
        } else {
            this.title = 'Create a Read';
            this.reads = new Reads({});
            this.mode = 'create';
            this.isLoaded = true;
        }
        this.getOrganizations();
        this.getCategoryList();
        this.createCourseEditForm(new Reads({}));

        this.config = {
            toolbar: [
                { name: 'formatting', items: ['PasteFromWord'] },
                {
                    name: 'basicstyles',
                    items: ['Bold', 'Italic', 'Underline', 'Strike', '-'],
                },
                { name: 'links', items: ['Link', 'Unlink'] },
                { name: 'paragraph', items: ['NumberedList', 'BulletedList'] },
            ],
        };
    }

    ngOnInit(): void {
        this.getReadById();
    }
    getCategoryList() {
        this.api.get('series/categories').subscribe(
            (result: any) => {
                this.categoryList = result;
                this.isLoaded = true;
            },
            (error: any) => {
                this.log.error('Error getting categories. ' + error.message);
            }
        );
    }

    getReadById() {
        if (this.readsId) {
            let payLoad: any = {};
            if (this.seriesPageType == 'customType') {
                payLoad['CourseType'] = 'organization';
            }
            this.api.get('series/reads/' + this.readsId, payLoad).subscribe(
                (result: any) => {
                    this.reads = new Reads(result[0]);
                    this.isLoaded = true;
                    this.createCourseEditForm(this.reads);
                    this.showTitleFrench = !!this.reads.titlefr?.trim();
                    this.showTitleSpanish = !!this.reads.titlees?.trim();
                    this.isLoaded = true;
                },
                (error: any) => {
                    this.log.error(
                        'Error getting organizations. ' + error.message
                    );
                }
            );
        }
    }

    get f() {
        return this.readsEditForm.controls;
    }

    getOrganizations() {
        this.api.get('admin/organizations', { Active: true }).subscribe(
            (results: any) => {
                this.organizations = Organization.initializeArray(results.data);
                let orgList = [];
                this.organizations.forEach(org => {
                    orgList.push(new MultiAutoItem(org.id, org.name));
                });
                this.multiSelOrgs = [...orgList];
            },
            (error: any) => {
                this.log.error('Error loading. ' + error.message);
            }
        );
    }

    createCourseEditForm(data: any) {
        this.readsEditForm = this.formBuilder.group({
            id: [data.id || null],
            titleen: [
                data.titleen || '',
                [Validators.required, Validators.maxLength(50)],
            ],
            titlefr: [data.titlefr || ''],
            titlees: [data.titlees || ''],
            isCompleted: [data.isCompleted || false],
            active: [data.active || false],
            file: this.createFileUpload(data.file),
            fileUrl: [data.fileUrl || ''],
            fileName: [data.fileName || ''],
            thumbnail: this.createFileUpload(data.thumbnail),
            thumbnailUrl: [data.thumbnailUrl || ''],
        });

        if (this.seriesPageType !== 'customType') {
            this.readsEditForm.addControl(
                'category',
                this.formBuilder.control(data.category || '', [
                    Validators.required,
                ])
            );
            this.readsEditForm.addControl(
                'default',
                this.formBuilder.control(data.default || false)
            );
        } else {
            this.readsEditForm.addControl(
                'orgIds',
                this.formBuilder.control(data.orgIds || '')
            );
        }
    }

    toggleField(language: string, field: string) {
        if (language === 'French') {
            if (field === 'Title') this.showTitleFrench = !this.showTitleFrench;
        } else if (language === 'Spanish') {
            if (field === 'Title')
                this.showTitleSpanish = !this.showTitleSpanish;
        }
    }

    createFileUpload(data: any) {
        return this.formBuilder.group({
            fileUpload: [data.fileUpload || null],
            fileFilename: [data.fileFilename || ''],
        });
    }

    changeAttachmentUploadListener($event): void {
        this.readFile($event.target, 'file', 'pdf');
    }

    readFile(
        inputValue: any,
        keyFileName: string,
        allowedTypes: string = 'pdf'
    ): void {
        this.isFileLoading = true;
        var file: File = inputValue.files[0];
        inputValue.files[0];
        let fileName = inputValue.files[0].name;
        var reader: FileReader = new FileReader();
        const maxFileSize = 25 * 1024 * 1024;
        const extPattern = new RegExp(`\.(${allowedTypes})$`, 'i');

        if (!extPattern.test(fileName)) {
            this.modalService.showAlert(
                'Error',
                `Invalid file extension. Allowed: ${allowedTypes}`
            );
            inputValue.value = '';

            this.isFileLoading = false;
            return;
        }

        if (file.size > maxFileSize) {
            this.modalService.showAlert(
                'Error',
                `File size exceeds 25MB. Please upload a smaller file.`
            );
            inputValue.value = '';
            this.isFileLoading = false;
            return;
        }

        reader.onloadend = () => {
            const readItem = this.readsEditForm.get(keyFileName) as FormGroup;

            readItem.patchValue({
                fileUpload: reader.result,
                fileFilename: fileName,
            });
            this.readsEditForm.value.fileUrl = '';
            this.readsEditForm.value.fileName = '';
            this.isFileLoading = false;
        };

        reader.readAsDataURL(file);
    }

    readsImageUpload($event) {
        var file: File = $event.target.files[0];
        $event.target.files[0];
        let fileName = $event.target.files[0].name;
        var reader: FileReader = new FileReader();
        const maxFileSize = 5 * 1024 * 1024;
        const readsImage = this.readsEditForm.get('thumbnail') as FormGroup;
        if (!/\.(jpg|jpeg)$/i.test(fileName)) {
            this.fileInput.nativeElement.value = '';
            this.modalService.showAlert(
                'Error',
                'Please ensure that the file you are uploading is in .jpg format.'
            );
            return;
        }

        if (file.size > maxFileSize) {
            this.fileInput.nativeElement.value = '';
            this.modalService.showAlert(
                'Error',
                'The file size exceeds the maximum limit of 2MB.'
            );
            return;
        }

        reader.onloadend = () => {
            readsImage.patchValue({
                fileUpload: reader.result,
                fileFilename: fileName,
            });
        };

        reader.readAsDataURL(file);
    }

    doSave() {
        this.reads = new Reads(this.readsEditForm.value);
        this.submitButtonPressed = true;
        if (!this.readsEditForm.valid) {
            this.readsEditForm.markAllAsTouched();
            return;
        }
        this.errors = '';

        if (
            !this.readsEditForm.value.file.fileFilename &&
            !this.readsEditForm.value.file.fileUpload &&
            !this.readsEditForm.value.fileUrl
        ) {
            this.fileError = true;
            this.errors = `The read file is mandatory`;
        }
        if (
            this.readsEditForm.get('titleen').value &&
            !this.readsEditForm.get('titleen').valid
        ) {
            this.errors = 'Invalid title';
            return;
        }

        if (this.errors || this.fileError) {
            return;
        }
        this.isLoaded = false;
        if (this.mode === 'update') {
            this.api
                .put(
                    'series/reads/' + this.readsId,
                    Reads.forAPI(this.reads, this.seriesPageType),
                    true
                )
                .subscribe(
                    (data: any) => {
                        this.modalService.showAlert(
                            'Success',
                            'Series has been updated'
                        );
                        this.isLoaded = true;
                        this.navigateToListingPage();
                    },
                    (error: any) => {
                        this.isLoaded = true;
                        this.modalService.showAlert(
                            'Error',
                            'Something went wrong. ' + error.message
                        );
                    }
                );
        } else {
            this.api
                .post(
                    'series/reads',
                    Reads.forAPI(this.reads, this.seriesPageType),
                    true
                )
                .subscribe(
                    (data: any) => {
                        this.modalService.showAlert(
                            'Success',
                            'Series has been created'
                        );
                        this.isLoaded = true;
                        this.navigateToListingPage();
                    },
                    (error: any) => {
                        this.isLoaded = true;
                        this.modalService.showAlert(
                            'Error',
                            'Something went wrong. ' + error.message
                        );
                    }
                );
        }
    }

    onSelectOrgs(items: MultiAutoItem[]) {
        let organizationIds = items.map(item => item.id);
        this.readsEditForm.patchValue({
            orgIds: organizationIds,
        });
    }

    onClose() {
        // this.dialogRef.close();
    }

    navigateToListingPage() {
        if (this.seriesPageType == 'customType') {
            this.router.navigate(['/admin/custom-series-listing']);
        } else {
            this.router.navigate(['/admin/series-listing']);
        }
    }
}
